import api from './client';
import { Result } from '../types/testData';
import { downloadBlob } from '../utils/download';
import type { PublicResult, PublicProcessResponse, WalletOnePaymentResponse, ConclusionListItem, ConclusionAccessResponse, ConclusionResponse, ResultRow, ResultDetail, CustomAnswerRow, PublicComparisonState } from './types';

export const submitResult = async (testId: number, data: Partial<Result>): Promise<Result> => {
  const response = await api.post(`/results?test_id=${testId}`, data);
  return response.data;
};

export const getMyResults = async (): Promise<Result[]> => {
  const response = await api.get('/results/my');
  return response.data;
};

export const getAllResults = async (
  page = 1,
  pageSize = 200,
): Promise<{ items: ResultRow[]; total: number }> => {
  const response = await api.get(`/results/all?page=${page}&page_size=${pageSize}`);
  return response.data;
};

export const getResultsDetails = async (ids: number[]): Promise<ResultDetail[]> => {
  const response = await api.get(`/results/details?ids=${ids.join(',')}`);
  return response.data;
};

export function _filenameFromResponse(response: unknown, fallback: string): string {
  const headers = (response as { headers?: Record<string, unknown> | undefined })?.headers;
  const cd = typeof headers?.['content-disposition'] === 'string' ? headers['content-disposition'] : '';
  const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1].trim());
  const ascii = cd.match(/filename="([^"]+)"/i);
  if (ascii) return ascii[1].trim();
  return fallback;
}

export const exportSelectedResults = async (ids: number[], withParams: boolean): Promise<void> => {
  const response = await api.get(
    `/results/export-selected?ids=${ids.join(',')}&with_params=${withParams}`,
    { responseType: 'blob' },
  );
  const filename = _filenameFromResponse(response, `results-${new Date().toISOString().slice(0, 10)}.csv`);
  downloadBlob(new Blob([response.data], { type: 'text/csv' }), filename);
};

export const deleteSelectedResults = async (ids: number[]): Promise<number> => {
  const response = await api.delete(`/results/delete-selected?ids=${ids.join(',')}`);
  return response.data.deleted;
};

export const getResultByCode = async (code: string): Promise<PublicResult> => {
  const response = await api.get(`/results/by-code/${code}`);
  return response.data;
};

/** Проверка кода индивидуального заключения для публичного сравнения. */
export const checkCompareCode = async (code: string): Promise<{ found: boolean; has_text: boolean; test_id?: number | null; test_title?: string | null; code_type?: 'individual' | 'comparison' | 'none' }> => {
  const response = await api.get(`/compare/check/${code}`);
  return response.data;
};

/** Создаёт (идемпотентно) публичное заключение-сравнение по двум кодам.
 *  purpose — необязательная цель сравнения: purpose_code (код из перечня),
 *  purpose_description (свободный текст), additional_notes.
 */
export const prepareComparison = async (
  code1: string,
  code2: string,
  purpose?: { purpose_code?: string | null; purpose_description?: string | null; additional_notes?: string | null },
): Promise<{ status: string; conclusion_id: number; compare_code: string; title: string }> => {
  const response = await api.post('/compare/prepare', {
    code1,
    code2,
    purpose_code: purpose?.purpose_code ?? null,
    purpose_description: purpose?.purpose_description ?? null,
    additional_notes: purpose?.additional_notes ?? null,
  });
  return response.data;
};

/** Запускает генерацию публичного сравнения (kind='public'). */
export const generateComparison = async (code: string): Promise<PublicProcessResponse> => {
  const response = await api.post(`/compare/by-code/${code}/generate`);
  return response.data;
};

/** Публичный статус заключения-сравнения по коду. */
export const getComparisonByCode = async (code: string): Promise<PublicComparisonState> => {
  const response = await api.get(`/compare/by-code/${code}`);
  return response.data;
};

/** Запускает фоновую генерацию заключения по коду сессии (публичный флоу). */
export const processResultByCode = async (code: string): Promise<PublicProcessResponse> => {
  const response = await api.post(`/results/by-code/${code}/process`);
  return response.data;
};

export const payResultByCode = async (code: string): Promise<WalletOnePaymentResponse> => {
  const response = await api.post(`/payments/by-code/${code}/pay`);
  return response.data;
};

export const createConclusion = async (resultIds: number[], type: string): Promise<ConclusionResponse> => {
  const response = await api.post('/conclusions', { result_ids: resultIds, type });
  return response.data;
};

export const getConclusion = async (id: number): Promise<ConclusionResponse> => {
  const response = await api.get(`/conclusions/${id}`);
  return response.data;
};

export const getResultDetail = async (id: number): Promise<ResultDetail> => {
  const response = await api.get(`/results/${id}/detail`);
  return response.data;
};

export const updateConclusion = async (id: number, text: string): Promise<ConclusionResponse> => {
  const response = await api.put(`/conclusions/${id}`, { text });
  return response.data;
};

export const generateConclusion = async (id: number, text?: string): Promise<{ status: string; conclusion_id?: number; job_id?: number; generation_status?: string }> => {
  const response = await api.post(`/conclusions/${id}/generate`, text !== undefined ? { text } : {});
  return response.data;
};

export const getCustomAnswers = async (conclusionId: number): Promise<CustomAnswerRow[]> => {
  const response = await api.get(`/conclusions/${conclusionId}/custom-answers`);
  return response.data;
};

export const saveCustomAnswer = async (
  conclusionId: number,
  questionId: number,
  data: { result_id: number; coefficient: string; parameters: Record<string, string> }
): Promise<void> => {
  await api.put(`/conclusions/${conclusionId}/custom-answers/${questionId}`, data);
};

export const deleteConclusion = async (id: number): Promise<void> => {
  await api.delete(`/conclusions/${id}`);
};

export interface ConclusionPdfResponse {
  url?: string;
  path?: string;
  status?: string;
  pdf_url?: string | null;
  pdf_ready?: boolean;
}

/** Generates/registers the PDF. This is deliberately separate from downloading it. */
export const saveConclusionPdf = async (id: number): Promise<ConclusionPdfResponse> => {
  const { data } = await api.post<ConclusionPdfResponse>(`/conclusions/${id}/pdf`);
  return data;
};

/** Downloads a registered PDF through the authenticated, payment-aware endpoint. */
export const downloadConclusionPdf = async (id: number, fallbackName?: string): Promise<void> => {
  const response = await api.get(`/pdf/conclusions/${id}/download`, { responseType: 'blob' });
  downloadBlob(response.data as Blob, _filenameFromResponse(response, fallbackName || 'conclusion.pdf'));
};

export const listConclusions = async (): Promise<ConclusionListItem[]> => {
  const response = await api.get('/conclusions');
  return response.data;
};

/** Статистика очереди генерации (сколько запросов к LLM сейчас обрабатывается/ждёт). */
export const getGenerationJobsStats = async (): Promise<{ queued: number; processing: number }> => {
  const response = await api.get('/conclusions/jobs/stats');
  return response.data;
};

interface ConclusionAccessPatch {
  public_download_limit?: number;
  allow_unpaid?: boolean;
  is_public?: boolean;
  // Номер версии (итерации) для изменения видимости.
  version?: number;
}

export const patchConclusionAccess = async (id: number, data: ConclusionAccessPatch): Promise<ConclusionAccessResponse> => {
  const response = await api.patch(`/conclusions/${id}/access`, data);
  return response.data;
};

export const markConclusionPaid = async (id: number): Promise<ConclusionAccessResponse> => {
  const response = await api.post(`/conclusions/${id}/mark-paid`);
  return response.data;
};

/** Снять пометку ручной оплаты заключения (admin, тестовый сценарий). */
export const unmarkConclusionPaid = async (id: number): Promise<ConclusionAccessResponse> => {
  const response = await api.post(`/conclusions/${id}/unmark-paid`);
  return response.data;
};

export const resetConclusionDownloads = async (id: number): Promise<ConclusionAccessResponse> => {
  const response = await api.post(`/conclusions/${id}/reset-downloads`);
  return response.data;
};
